import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { getOtherDistanceUnits, useDistanceUnits } from '../../../utils/distanceUnits';
import Legend from '../../Legend';
import { ChangeUnitsButton } from '../ChangeUnitsButton';
import { geojson, linestring } from '../map.constants';
import { CLICKABLE_COUNTRY_QUERY } from '../operations';
import { RulerButton } from '../RulerButton';
import Settings from '../Settings';
import { MAP_TILESET_TYPES } from '../Utils/constants';
import { getUserPreferenceMapStyle } from '../Utils/mapStyles';
import { DARK_MAP_STYLE } from '../Utils/mapStyleTypes';
import { createMap, rulerEvent } from '../Utils/utils';

const useStyles = makeStyles(() => ({
  mapContainer: {
    width: '100%',
    height: '100%',
  },
  hiddenContainer: {
    visibility: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
  },
}));

const StandardMap = ({
  mapContainerId = 'mapContainer',
  map,
  setMap,
  style = DARK_MAP_STYLE.value,
  setStyle = () => {},
  layers = [],
  layerFilters = [],
  layerStyle,
  hideLayers = [],
  enableEntityHighlight,
  enableInfoPopup,
  layerClickCallback,
  legendProps = {},
  control,
  ruler,
  styles = {},
  settingsProps = {},
  syncMap,
  callbacks = {},
  countryClickProps = {},
  removeLayerCallback = () => {},
  defaultMapPosition,
  tilesetType = MAP_TILESET_TYPES.oilAndGasMap,
}) => {
  const { flagsLoaded, inDevelopment } = useGetFeatureFlags();
  const classes = useStyles();
  const mapPosition = useParams();
  const { currentUser } = useCurrentUser();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [rulerActive, setRulerActive] = useState(false);
  const { distanceUnits, setDistanceUnits } = useDistanceUnits();

  const onDistanceUnitsChange = useCallback(() => {
    const newMetric = getOtherDistanceUnits(distanceUnits);
    setDistanceUnits(newMetric);
    map._scaleControl?.setUnit(newMetric);
    map.onClickRulerCallback = rulerEvent(map, geojson, linestring, newMetric);
  }, [map, distanceUnits, setDistanceUnits]);

  // prepend legend to mapbox controls container if legend is enabled or we want to keep the legend mounted
  const setLegend = useCallback(
    node => {
      if (mapLoaded && node) {
        const controlContainer = document.getElementsByClassName('mapboxgl-control-container')[0];
        const bottomRightControlContainer = document.getElementsByClassName(
          'mapboxgl-ctrl-bottom-right',
        )[0];
        controlContainer.insertBefore(node, bottomRightControlContainer);
      }
    },
    [mapLoaded],
  );

  const { data } = useQuery(CLICKABLE_COUNTRY_QUERY, {
    variables: {
      filters: countryClickProps.filters,
      selectedCountry: countryClickProps.selectedCountry,
    },
    skip: !countryClickProps.enabled,
  });

  const mapStyle = useMemo(() => getUserPreferenceMapStyle(style), [style]);

  const tilesetSources = useMemo(() => {
    switch (tilesetType) {
      case MAP_TILESET_TYPES.oilAndGasMap:
        return currentUser?.mapTilesetSources;
      case MAP_TILESET_TYPES.ccus:
        return currentUser?.mapCcusTilesetSources;
      default:
        return currentUser?.mapTilesetSources;
    }
  }, [tilesetType, currentUser]);

  useEffect(() => {
    if (map) {
      Object.defineProperty(map, 'getTilesetType', {
        value: () => tilesetType,
        writable: false,
        configurable: true,
      });
    }
  }, [map, tilesetType]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress dependency of map vars
  useEffect(() => {
    if (tilesetSources && flagsLoaded) {
      const enableCountryClick = !!countryClickProps.enabled;
      const newMap = createMap({
        callbacks,
        control,
        enableCountryClick,
        enableEntityHighlight,
        enableInfoPopup,
        geojson,
        hideLayers,
        initialMetric: layerStyle?.metric,
        initialSizeType: layerStyle?.sizeType,
        latLong: defaultMapPosition?.latLong || mapPosition.latLong,
        layerClickCallback,
        layerFilters,
        layers,
        linestring,
        mapContainerId,
        mapTilesetSources: tilesetSources,
        ruler,
        setMapLoaded,
        setRulerActive,
        setStyle,
        style: mapStyle,
        zoom: defaultMapPosition?.zoom || mapPosition.zoom,
        defaultDistanceMetric: distanceUnits,
      });
      setMap(newMap);
    }
  }, [tilesetSources, flagsLoaded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(map): suppress dependency of map
  useEffect(() => {
    if (inDevelopment && mapLoaded) {
      window.map = map;
    }
  }, [inDevelopment, mapLoaded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(map.layerCallbacks.setCountryBoundaryStyle): suppress dependency of map.layerCallbacks.setCountryBoundaryStyle
  useEffect(() => {
    if (mapLoaded && data && data.mapInteractiveCountries?.feature) {
      map.layerCallbacks.setCountryBoundaryStyle(data.mapInteractiveCountries.feature);
    }
  }, [data, mapLoaded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(map.layerCallbacks.filterLayers): suppress dependency of map.layerCallbacks.filterLayers
  useEffect(() => {
    if (mapLoaded && layerFilters.length > 0) {
      map.layerCallbacks.filterLayers(layerFilters);
    }
  }, [layerFilters, mapLoaded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(map.layerCallbacks.changeLayerStyle): suppress dependency of map.layerCallbacks.changeLayerStyle
  useEffect(() => {
    if (mapLoaded && layerStyle) {
      const { layer, metric, sizeType } = layerStyle;
      map.layerCallbacks.changeLayerStyle(layer, metric, sizeType);
    }
  }, [layerStyle, mapLoaded]);

  const toggleMapStyle = newStyle => {
    setMapLoaded(false);
    map.toggleMapStyle(newStyle);
    if (syncMap) {
      syncMap.toggleMapStyle(newStyle);
    }
  };

  const addLayer = (layer, style) => {
    map.layerCallbacks[layer.call](style);
    if (syncMap) {
      syncMap.layerCallbacks[layer.call](style);
    }
  };

  const removeLayer = layer => {
    map.layerCallbacks[layer.call]();
    removeLayerCallback();
    if (syncMap) {
      syncMap.layerCallbacks[layer.call]();
    }
  };

  const toggleLayer = (layer, visibility) => {
    map.layerCallbacks[layer.call](layer.entityTypes, visibility);
    if (syncMap) {
      syncMap.layerCallbacks[layer.call](layer.entityTypes, visibility);
    }
  };

  return (
    <>
      <div
        id={mapContainerId}
        className={clsx({
          [classes.mapContainer]: true,
          [styles.mapContainer]: !!styles.mapContainer,
        })}
      />
      {/* biome-ignore lint/correctness/useUniqueElementIds: Required for mapbox initialization */}
      <div id='distanceContainer' />
      <div className={classes.hiddenContainer}>
        <div ref={legendProps.enabled || legendProps.keepMounted ? setLegend : () => {}}>
          {(legendProps.enabled || legendProps.keepMounted) && (
            <Legend styles={styles} multiline title {...legendProps} />
          )}
        </div>
        {ruler && (
          <RulerButton
            rulerActive={rulerActive}
            setRulerActive={setRulerActive}
            map={map}
            mapLoaded={mapLoaded}
            distanceUnits={distanceUnits}
          />
        )}
        {ruler && (
          <ChangeUnitsButton
            rulerActive={rulerActive}
            mapLoaded={mapLoaded}
            distanceUnits={distanceUnits}
            onDistanceUnitsChange={onDistanceUnitsChange}
          />
        )}
      </div>
      {settingsProps.enabled && (
        <Settings
          {...settingsProps}
          layerProps={{ ...settingsProps.layerProps, mapLoaded }}
          legendContainerRef={!(legendProps.enabled || legendProps.keepMounted) ? setLegend : null}
          addLayer={addLayer}
          mapStyle={mapStyle}
          removeLayer={removeLayer}
          toggleLayer={toggleLayer}
          toggleMapStyle={toggleMapStyle}
        />
      )}
    </>
  );
};

export default StandardMap;
